import React from 'react';
import { Box, Typography, FormHelperText, Button, Checkbox, Grid, CircularProgress } from '@mui/material';
import AlertDialog from '../Components/DialogBox/dialogbox';
import theme, { textClasses, buttonClasses } from '../../static/styles/theme';
import { useResponsive } from '../../utils/hooks';

export default function ConsentModal({ open, onDecline, onAgree, consentPrivacy, consentTerms, onPrivacyChange, onTermsChange, loading = false, darkMode = false }) {
    const { dialogWidth } = useResponsive();

    const colors = darkMode
        ? {
              paper: '#111',
              title: '#ffffff',
              subtitle: '#CFCFCF',
              text: '#ffffff',
              border: '#333',
              errorText: '#ff6b6b',
          }
        : {
              paper: undefined,
              title: undefined,
              subtitle: '#666',
              text: undefined,
              border: '#e0e0e0',
              errorText: undefined,
          };

    return (
        <AlertDialog
            viewDialog={open}
            handleClose={loading ? undefined : onDecline}
            title={
                <Typography sx={{ ...textClasses.cardTitle, mt: 1.5, mb: 0, ml: '7px', ...(darkMode && { color: colors.title }) }}>
                    Before you continue
                </Typography>
            }
            width={dialogWidth}
            borderRadius="16px"
            viewCloseIcon={false}
            actionsCSS={{ display: 'none' }}
            contentSx={{ padding: '6px 20px 20px 20px' }}
            bodyCSS={{ p: 0, fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}
            paperBg={colors.paper}
            paperSx={darkMode ? { boxShadow: '0 0 0 1px #3a3a3a, 0 8px 40px rgba(0,0,0,0.9)', border: '1px solid #2a2a2a' } : {}}
            body={
                <Box>
                    <Typography sx={{ color: colors.subtitle, mb: 2, fontSize: '14px' }}>
                        Please review and accept the following to sign in.
                    </Typography>

                    <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: '8px', p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                            checked={consentPrivacy}
                            onChange={(e) => onPrivacyChange(e.target.checked)}
                            disabled={loading}
                            size="small"
                            sx={{ p: 0, cursor: 'pointer', color: theme.themeOrange, '&.Mui-checked': { color: theme.themeOrange } }}
                        />
                        <Typography sx={{ fontSize: '14px', cursor: 'default', userSelect: 'none', ...(darkMode && { color: colors.text }) }}>
                            I consent to the processing of my personal data in accordance with the{' '}
                            <Box component="span" onClick={() => window.open(process.env.REACT_APP_PRIVACY_POLICY, '_blank')} sx={{ color: theme.themeStatusPurple, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                                Privacy Policy
                            </Box>.
                        </Typography>
                    </Box>

                    <Box sx={{ border: `1px solid ${colors.border}`, borderRadius: '8px', p: 1.5, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                            checked={consentTerms}
                            onChange={(e) => onTermsChange(e.target.checked)}
                            disabled={loading}
                            size="small"
                            sx={{ p: 0, cursor: 'pointer', color: theme.themeOrange, '&.Mui-checked': { color: theme.themeOrange } }}
                        />
                        <Typography sx={{ fontSize: '14px', cursor: 'default', userSelect: 'none', ...(darkMode && { color: colors.text }) }}>
                            I have read and agree to the{' '}
                            <Box component="span" onClick={() => window.open(process.env.REACT_APP_TERMS_OF_USE, '_blank')} sx={{ color: theme.themeStatusPurple, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
                                Terms of Use
                            </Box>.
                        </Typography>
                    </Box>

                    {(!consentPrivacy || !consentTerms) && (
                        <FormHelperText sx={{ fontSize: '13px', mb: 1, color: colors.errorText || '#d32f2f' }}>
                            Please accept both the Privacy Policy and Terms of Use to continue.
                        </FormHelperText>
                    )}

                    <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        <Grid item xs={3}>
                            <Button
                                fullWidth
                                disabled={loading}
                                onClick={onDecline}
                                sx={darkMode ? {
                                    backgroundColor: '#0E0E0E99',
                                    color: theme.themeOrange,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    borderRadius: '2px',
                                    border: `1px solid ${theme.themeOrange}`,
                                    py: 1,
                                    '&:hover': { backgroundColor: '#2a2a2a' },
                                    '&.Mui-disabled': { backgroundColor: '#0E0E0E99', color: theme.themeOrange, opacity: 0.5, pointerEvents: 'auto', cursor: 'not-allowed' },
                                } : { ...buttonClasses.lynkitOrangeEmpty, py: 1, '&.Mui-disabled': { pointerEvents: 'auto', cursor: 'not-allowed' } }}
                            >
                                Decline
                            </Button>
                        </Grid>
                        <Grid item xs={3}>
                            <Button
                                fullWidth
                                disabled={!consentPrivacy || !consentTerms || loading}
                                onClick={onAgree}
                                sx={darkMode ? {
                                    backgroundColor: '#0E0E0E99',
                                    color: theme.themeOrange,
                                    textTransform: 'none',
                                    fontWeight: 900,
                                    borderRadius: '2px',
                                    border: `1px solid ${theme.themeOrange}`,
                                    py: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1,
                                    '&:hover': { backgroundColor: '#2a2a2a' },
                                    '&.Mui-disabled': { backgroundColor: '#0E0E0E99', color: theme.themeOrange, opacity: 0.5, pointerEvents: 'auto', cursor: 'not-allowed', border: `1px solid ${theme.themeOrange}` },
                                } : { ...buttonClasses.lynkitOrangeFill, py: 1 }}
                            >
                                {loading ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CircularProgress size={14} sx={{ color: 'inherit' }} />
                                        Signing In...
                                    </Box>
                                ) : 'Agree & Sign In'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            }
        />
    );
}
